import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Mail, Phone, MapPin, Building2, Edit3, Trash2, Maximize2, Globe,
  ListPlus, Sparkles, Star, Video,
} from "lucide-react";
import { SOCIAL_PLATFORMS, socialHref, externalHref } from "@/lib/socials";
import { imageSrc } from "@/lib/images";

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export function ContactCard({ contact, lists, onExpand, onEdit, onDelete, onToggleList }) {
  const r = contact.record || {};
  const socials = contact.socials || {};
  const isPerson = contact.kind === "person";
  const [imageBroken, setImageBroken] = useState(false);
  // A deleted or dead image falls back to initials instead of a broken-image icon.
  const photo = imageBroken ? null : imageSrc(isPerson ? r.photo_url : r.logo_url);
  const socialKeys = SOCIAL_PLATFORMS.filter(({ key }) => socials[key]).slice(0, 5);

  return (
    <Card className="studio-card p-5 min-h-[292px] max-h-[292px] overflow-hidden flex flex-col" data-testid={`contact-card-${contact.id}`}>
      <div className="flex items-start gap-3">
        {isPerson ? (
          photo ? (
            <img src={photo} alt={contact.name} data-testid={`contact-photo-${contact.id}`}
                 onError={() => setImageBroken(true)}
                 className="h-11 w-11 rounded-full object-cover shrink-0 border border-border" />
          ) : (
            <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[hsl(var(--studio-primary))] to-[hsl(var(--studio-secondary))] text-primary-foreground text-sm font-semibold flex items-center justify-center shrink-0">
              {initials(contact.name)}
            </div>
          )
        ) : photo ? (
          <img src={photo} alt={contact.name} data-testid={`contact-photo-${contact.id}`}
               onError={() => setImageBroken(true)}
               className="h-11 w-11 rounded-lg object-contain bg-white/5 shrink-0 border border-border p-0.5" />
        ) : (
          <div className="h-11 w-11 rounded-lg border border-border bg-accent/40 flex items-center justify-center font-semibold shrink-0">
            {contact.name?.charAt(0)?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="font-medium truncate" title={contact.full_name || contact.name}>
              {contact.full_name || contact.name}
            </div>
            {isPerson ? (
              r.profile_mode === "advanced" && (
                <span title="Advanced profile" className="text-[hsl(var(--studio-secondary))] shrink-0"><Sparkles className="w-3.5 h-3.5"/></span>
              )
            ) : (
              <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            )}
          </div>
          {contact.nickname && (
            <div className="text-[11px] text-muted-foreground truncate" data-testid={`contact-nickname-${contact.id}`}>
              Goes by “{contact.nickname}”
            </div>
          )}
          {contact.subtitle && <div className="text-xs text-muted-foreground truncate">{contact.subtitle}</div>}
          {contact.location && (
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
              <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{contact.location}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-wrap gap-1 max-h-[26px] overflow-hidden">
        <Badge variant={isPerson ? "secondary" : "outline"} className="text-[10px] font-normal">
          {isPerson ? "person" : "company"}
        </Badge>
        {(contact.company_names || []).slice(0, 1).map((n) => (
          <Badge key={n} variant="outline" className="text-[10px] font-normal max-w-[140px] truncate">{n}</Badge>
        ))}
        {!isPerson && (contact.people || []).length > 0 && (
          <Badge variant="secondary" className="text-[10px] font-normal">
            {contact.people.length} contact{contact.people.length === 1 ? "" : "s"}
          </Badge>
        )}
        {(contact.list_names || []).slice(0, 2).map((n) => (
          <Badge key={n} className="text-[10px] font-normal max-w-[140px] truncate" data-testid={`contact-list-badge-${contact.id}`}>{n}</Badge>
        ))}
        {(r.credits || []).length > 0 && (
          <Badge variant="outline" className="text-[10px] font-normal flex items-center gap-1">
            <Star className="w-2.5 h-2.5"/> {r.credits.length}
          </Badge>
        )}
      </div>

      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        {contact.email && (
          <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-foreground truncate">
            <Mail className="w-3 h-3 shrink-0"/> <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-foreground truncate">
            <Phone className="w-3 h-3 shrink-0"/> <span className="truncate">{contact.phone}</span>
          </a>
        )}
        {contact.website && (
          <a href={externalHref(contact.website)} target="_blank" rel="noreferrer"
             data-testid={`contact-website-${contact.id}`}
             className="flex items-center gap-1.5 hover:text-foreground truncate">
            <Globe className="w-3 h-3 shrink-0"/> <span className="truncate">website</span>
          </a>
        )}
      </div>

      {(socialKeys.length > 0 || r.showreel_url) && (
        <div className="mt-2 flex items-center gap-1.5" data-testid={`contact-socials-${contact.id}`}>
          {socialKeys.map(({ key, label, icon: Icon }) => (
            <a key={key} href={socialHref(key, socials[key])} target="_blank" rel="noreferrer"
               title={`${label}: ${socials[key]}`} data-testid={`contact-social-${key}-${contact.id}`}
               className="grid h-7 w-7 place-items-center rounded-md border border-border text-muted-foreground hover:border-[hsl(var(--studio-primary)/0.45)] hover:text-[hsl(var(--studio-primary))]">
              <Icon className="h-3.5 w-3.5" />
            </a>
          ))}
          {r.showreel_url && (
            <a href={externalHref(r.showreel_url)} target="_blank" rel="noreferrer" title="Showreel"
               data-testid={`contact-showreel-${contact.id}`}
               className="grid h-7 w-7 place-items-center rounded-md border border-border text-muted-foreground hover:border-[hsl(var(--studio-primary)/0.45)] hover:text-[hsl(var(--studio-primary))]">
              <Video className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      )}
      </div>

      <div className="mt-auto pt-3 border-t border-border flex items-center gap-1">
        <Button variant="ghost" size="sm" data-testid={`contact-expand-${contact.id}`} onClick={() => onExpand(contact)}>
          <Maximize2 className="w-3.5 h-3.5 mr-1"/> Expand
        </Button>
        <div className="ml-auto flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid={`contact-lists-${contact.id}`}><ListPlus className="w-3.5 h-3.5"/></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Lists</DropdownMenuLabel>
              {lists.length === 0 && <DropdownMenuItem disabled>No lists yet</DropdownMenuItem>}
              {lists.map((l) => {
                const inList = (contact.list_ids || []).includes(l.id);
                return (
                  <DropdownMenuItem key={l.id} data-testid={`contact-list-toggle-${contact.id}-${l.id}`}
                                    onClick={() => onToggleList(contact, l, !inList)}>
                    {inList ? "✓ " : ""}{l.name}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" data-testid={`contact-edit-${contact.id}`} onClick={() => onEdit(contact)}><Edit3 className="w-3.5 h-3.5"/></Button>
          <Button variant="ghost" size="sm" data-testid={`contact-delete-${contact.id}`} onClick={() => onDelete(contact)}><Trash2 className="w-3.5 h-3.5"/></Button>
        </div>
      </div>
    </Card>
  );
}
